import { computed, ref, type Ref } from 'vue'
import { useWebSocket } from '@/composable/useWebsockets'
import { useAuthStore } from '@/stores/authStore'
import { useEyeDexStore } from '@/stores/eyeDexStore'
import { useMyToast } from '@/composable/useMyToast'
import { i18n } from '@/i18n'
import type {
  DexPoolChain,
  DexPoolLiveData,
  DexPriceByVolMsg,
  DexScreenerPoolItem,
  DexTvlMsg,
} from '@/services/dex-pools-v2-service/types'
import type { WsSubRequest, WsSubResponse } from '@/utils/types'
import { roundUsdToTier } from '@/utils/roundUsdToTier'
import { ALLOWED_QUOTE_MINTS, TOKEN_SYMBOLS } from '@/utils/dexPoolConsts'
import { capitalize } from 'lodash'

export interface ComputedPool {
  address: string
  dexName: string
  dexId: string
  liquidityUsd: number | null
  vol24h: number | null
  spotPrice: number | null
  buyPrice: number | null
  sellPrice: number | null
  tvl: number | null
  priceUsd: number | null
  ts: number | null
  baseSymbol: string | null
  quoteSymbol: string | null
  dexParser?: string
  state?: Record<string, unknown>
}

const MIN_LIQUIDITY = 10000

export const usePoolsData = (
  chain: Ref<string>,
  contract: Ref<string>,
  amountUsd: Ref<number> = ref(100),
) => {
  const authStore = useAuthStore()
  const eyeDexStore = useEyeDexStore()
  const { addToast } = useMyToast()
  const { t } = i18n.global

  const loading = ref(false)
  const dexPoolsData = ref<Record<string, DexPoolLiveData>>({})
  const poolsList = ref<DexScreenerPoolItem[]>([])
  const wsAttempts = ref(0)

  const onClose = (reconnect: () => void) => {
    wsAttempts.value += 1
    if (wsAttempts.value % 5 === 0) {
      addToast({ severity: 'error', message: t('alerts.connectionLost') })
    }
    window.setTimeout(() => reconnect(), 1200)
  }

  const onOpen = () => {
    wsAttempts.value = 0
  }

  const onMessage = async (message: string) => {
    const response: WsSubResponse = JSON.parse(message)
    if (response.event !== 'watch') return
    const params = response.params
    if (params[0] === 'dex-price-by-vol' && params[2]) {
      const poolKey = params[2].toLowerCase()
      const data = response.data as DexPriceByVolMsg
      const existing = dexPoolsData.value[poolKey] ?? {
        pool: poolKey,
        dex: '',
        priceBuy: null,
        priceSell: null,
        tvl: null,
        ts: null,
      }
      dexPoolsData.value[poolKey] = {
        ...existing,
        priceBuy: data.buy,
        priceSell: data.sell,
        ts: data.ts,
      }
    } else if (params[0] === 'dex-tvl' && params[2]) {
      const poolKey = params[2].toLowerCase()
      const data = response.data as DexTvlMsg
      const existing = dexPoolsData.value[poolKey] ?? {
        pool: poolKey,
        dex: '',
        priceBuy: null,
        priceSell: null,
        tvl: null,
        ts: null,
      }
      dexPoolsData.value[poolKey] = {
        ...existing,
        tvl: data.tvl,
        ts: existing.ts ?? data.ts,
      }
    }
  }

  const { run, stop, status } = useWebSocket(
    import.meta.env.VITE_WS_URL + '/api/v2/ws?token=' + authStore.token,
    {
      onMessage,
      handleClose: onClose,
      handleOpen: onOpen,
    },
  )

  /** Consensus symbol for the searched contract across all pools. */
  const targetTokenSymbol = computed(() => {
    const target = contract.value.toLowerCase()
    const counts: Record<string, number> = {}

    for (const p of poolsList.value) {
      const baseAddr = p.base_token_address?.toLowerCase()
      const quoteAddr = p.quote_token_address?.toLowerCase()

      if (baseAddr === target && p.base_token_symbol) {
        counts[p.base_token_symbol] = (counts[p.base_token_symbol] || 0) + 1
      }
      if (quoteAddr === target && p.quote_token_symbol) {
        counts[p.quote_token_symbol] = (counts[p.quote_token_symbol] || 0) + 1
      }
    }

    let bestSymbol: string | null = null
    let bestCount = 0
    for (const [sym, count] of Object.entries(counts)) {
      if (count > bestCount) {
        bestCount = count
        bestSymbol = sym
      }
    }
    return bestSymbol
  })

  const loadPools = async () => {
    if (!chain.value || !contract.value) return

    stop()
    dexPoolsData.value = {}
    poolsList.value = []
    loading.value = true

    const chainStr = chain.value.toLowerCase() as DexPoolChain

    try {
      const pools = await eyeDexStore.fetchDexScreenerPools(
        `pools/${chainStr}/${contract.value}`,
        chainStr,
        contract.value,
      )

      const allowedMints = ALLOWED_QUOTE_MINTS[chainStr]

      const filtered = (pools ?? []).filter((p) => {
        if ((p.liquidity_usd ?? 0) <= 1) return false

        if (allowedMints) {
          const base = p.base_token_address?.toLowerCase()
          const quote = p.quote_token_address?.toLowerCase()
          const isAllowed = (addr: string | undefined) =>
            addr ? allowedMints.has(addr) : false
          if (!isAllowed(base) && !isAllowed(quote)) return false
        }

        return true
      })

      filtered.sort((a, b) => (b.liquidity_usd ?? 0) - (a.liquidity_usd ?? 0))
      poolsList.value = filtered

      const amountTier = roundUsdToTier(amountUsd.value)
      const subMessages: Array<WsSubRequest> = []
      filtered.forEach((p) => {
        subMessages.push({
          action: 'watch',
          params: ['dex-price-by-vol', chainStr, p.pair_address, amountTier],
        })
        subMessages.push({
          action: 'watch',
          params: ['dex-tvl', chainStr, p.pair_address],
        })
      })

      if (subMessages.length > 0) {
        run(JSON.stringify(subMessages))
      }
    } catch (e) {
      console.error('Failed to load pools', e)
      addToast({ severity: 'error', message: t('loadingError') })
    } finally {
      loading.value = false
    }
  }

  const stopPools = () => {
    stop()
    dexPoolsData.value = {}
    poolsList.value = []
  }

  const normalizeSymbol = (
    addr: string | null | undefined,
    rawSymbol: string | null | undefined,
  ): string | null => {
    const lowerAddr = addr?.toLowerCase()
    if (!lowerAddr) return rawSymbol ?? null

    // Known stable / native mints → canonical symbol
    if (TOKEN_SYMBOLS[lowerAddr]) return TOKEN_SYMBOLS[lowerAddr]

    // Target token → consensus symbol across pools
    if (lowerAddr === contract.value.toLowerCase()) {
      return targetTokenSymbol.value ?? rawSymbol ?? null
    }

    return rawSymbol ?? null
  }

  const pools = computed<ComputedPool[]>(() => {
    return poolsList.value.map((p) => {
      const key = p.pair_address.toLowerCase()
      const data = dexPoolsData.value[key]

      let spotPrice: number | null = null
      let buyPrice: number | null = null
      let sellPrice: number | null = null
      let tvl: number | null = null
      let ts: number | null = null

      if (data) {
        buyPrice = data.priceBuy
        sellPrice = data.priceSell
        tvl = data.tvl ?? null
        ts = data.ts ?? null
        spotPrice = data.priceBuy ?? data.priceSell ?? null
      } else {
        // Fallback to DexScreener data when live data is not available
        const dexPrice = p.price_usd ? Number(p.price_usd) : null
        if (dexPrice != null && Number.isFinite(dexPrice) && dexPrice > 0) {
          spotPrice = dexPrice
          buyPrice = dexPrice
          sellPrice = dexPrice
        }
        tvl = p.liquidity_usd ?? null
      }

      const dexName = capitalize(p.dex_id?.split('-')[0] ?? 'DEX')

      return {
        address: p.pair_address,
        dexName,
        dexId: p.dex_id ?? '',
        liquidityUsd: p.liquidity_usd ?? null,
        vol24h: p.vol24h ?? null,
        spotPrice,
        buyPrice,
        sellPrice,
        tvl,
        priceUsd: p.price_usd ? Number(p.price_usd) : null,
        ts,
        baseSymbol: normalizeSymbol(p.base_token_address, p.base_token_symbol),
        quoteSymbol: normalizeSymbol(p.quote_token_address, p.quote_token_symbol),
        dexParser: p.dex_id ?? undefined,
        state: (() => {
          const s: Record<string, unknown> = {}
          const asset = p.base_token_address
          const quote = p.quote_token_address
          if (!asset || !quote) return undefined
          // TODO: fetch real token decimals from on-chain or API
          const baseDec = chain.value.toLowerCase() === 'sol' ? 9 : 18
          const quoteDec = chain.value.toLowerCase() === 'sol' ? 6 : 18
          s.token0 = asset
          s.token1 = quote
          s.dec0 = baseDec
          s.dec1 = quoteDec
          s.mint_x = asset
          s.mint_y = quote
          s.dec_x = baseDec
          s.dec_y = quoteDec
          s.coin = asset
          s.pc = quote
          s.coin_dec = baseDec
          s.pc_dec = quoteDec
          s.mint0 = asset
          s.mint1 = quote
          s.mint0_dec = baseDec
          s.mint1_dec = quoteDec
          s.base = asset
          s.quote = quote
          s.base_dec = baseDec
          s.quote_dec = quoteDec
          return s
        })(),
      }
    })
  })

  const filteredPools = computed(() =>
    pools.value.filter((p) => (p.liquidityUsd ?? 0) >= MIN_LIQUIDITY || (p.tvl ?? 0) >= MIN_LIQUIDITY),
  )

  return {
    pools: filteredPools,
    allPools: pools,
    loading,
    wsStatus: status,
    loadPools,
    stopPools,
    dexPoolsData,
  }
}
